// The AI-modification client contract. Mock and live implement the same
// interface; screens import the selected client from ./index and never know
// which one they got.
import type {
  AgreementPreview,
  AgreementPreviewRequest,
  ModificationAcceptanceFile,
  ModificationAgreementConsent,
  ModificationContractFile,
  ModificationCreated,
  ModificationDecisionBody,
  ModificationDecisionResult,
  ModificationDetail,
  ModificationEligibility,
  ModificationFileDeclaration,
  ModificationFileLink,
  ModificationLockResetBody,
  ModificationLockResetResult,
  ModificationSubmitBody,
  ModificationSubmitted,
  ModificationSummary,
  ModificationTerms,
  ModificationUploadSlot,
} from '../../models/modification.interface';

// How a failure reaches a screen. The gateway answers 403 as {"error":"forbidden"}
// and everything else as {"error":{"message":"..."}}; a 401 with no token has
// no body at all. Bodies are read defensively and only a string message is kept.
export type ModificationApiErrorKind =
  | 'auth' // 401
  | 'forbidden' // 403
  | 'not-found' // 404: a wrong id and someone else's look the same
  | 'conflict' // 409: the record moved on, or the state does not allow it
  | 'invalid' // 422: a field the server refused; serverMessage says which
  | 'unavailable' // 503: not configured
  | 'network' // no response
  | 'disabled' // the flag is off
  | 'unknown';

export class ModificationApiError extends Error {
  readonly kind: ModificationApiErrorKind;
  readonly status?: number;
  readonly serverMessage?: string;

  constructor(message: string, details: { kind: ModificationApiErrorKind; status?: number; serverMessage?: string }) {
    super(message);
    this.name = 'ModificationApiError';
    this.kind = details.kind;
    this.status = details.status;
    this.serverMessage = details.serverMessage;
  }
}

export const isModificationApiError = (e: unknown): e is ModificationApiError => e instanceof ModificationApiError;

/** The kind of any error a screen catches, whatever threw it. */
export const errorKindOf = (e: unknown): ModificationApiErrorKind => {
  const kind = e && typeof e === 'object' ? (e as { kind?: unknown }).kind : undefined;
  return typeof kind === 'string' ? (kind as ModificationApiErrorKind) : 'unknown';
};

/** The server's own message on a 422, when it sent a string. */
export const serverMessageOf = (e: unknown): string | null => {
  const message = e && typeof e === 'object' ? (e as { serverMessage?: unknown }).serverMessage : undefined;
  return typeof message === 'string' && message.length > 0 ? message : null;
};

export type UploadProgress = (fraction: number) => void;

export interface ModificationApi {
  /** E1 */
  getTerms(signal?: AbortSignal): Promise<ModificationTerms>;
  /** E2 */
  getEligibility(orderId: string, signal?: AbortSignal): Promise<ModificationEligibility>;
  /** E14: the filled addendum for a new request. */
  previewAgreement(body: AgreementPreviewRequest): Promise<AgreementPreview>;
  /** E3. The key is the caller's, the same one on a retry of the same attempt. The agreement, when the addendum is on. */
  createModification(orderId: string, idempotencyKey: string, agreement?: ModificationAgreementConsent): Promise<ModificationCreated>;
  /** E4 */
  issueUploadSlot(modificationId: string, file: ModificationFileDeclaration): Promise<ModificationUploadSlot>;
  /** The browser's PUT to the link E4 gave: exactly its headers, never a token. */
  uploadFile(
    slot: Pick<ModificationUploadSlot, 'uploadUrl' | 'requiredHeaders'>,
    file: File,
    onProgress?: UploadProgress,
    signal?: AbortSignal
  ): Promise<void>;
  /** E5 */
  submitRevision(modificationId: string, revision: number, body: ModificationSubmitBody, idempotencyKey: string): Promise<ModificationSubmitted>;
  /** E6 */
  listMine(query: { status?: string; orderId?: string }, signal?: AbortSignal): Promise<ModificationSummary[]>;
  /** E7 */
  getModification(modificationId: string, signal?: AbortSignal): Promise<ModificationDetail>;
  /** E8 */
  getRevisionFile(modificationId: string, revision: number): Promise<ModificationFileLink>;
  /** E9 */
  getContract(modificationId: string, signal?: AbortSignal): Promise<ModificationContractFile>;
  /** E15: one accepted addendum as a PDF. */
  getAcceptancePdf(modificationId: string, acceptanceId: string, signal?: AbortSignal): Promise<ModificationAcceptanceFile>;
  /** E10 */
  listForLicensor(query: { status?: string }, signal?: AbortSignal): Promise<ModificationSummary[]>;
  /** E11 */
  decide(modificationId: string, body: ModificationDecisionBody, idempotencyKey: string): Promise<ModificationDecisionResult>;
  /** E12 */
  resetLock(modificationId: string, body: ModificationLockResetBody, idempotencyKey: string): Promise<ModificationLockResetResult>;
  /** E13 */
  listForAdmin(query: { status?: string; stale?: boolean }, signal?: AbortSignal): Promise<ModificationSummary[]>;
}

/**
 * A fresh Idempotency-Key for one attempt. The screen keeps it and sends the
 * same one if it retries that attempt, so a lost answer never creates twice.
 * Uniqueness is what matters, not secrecy: the key is scoped server side to the
 * caller and the route.
 */
export const newIdempotencyKey = (): string => {
  const cryptoApi = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};
